import BagongPilipinasLogo from '@/assets/BAGONG-PILIPINAS-LOGO.png';
import DENRLogo from '@/assets/denr-logo.png';
import { format } from 'date-fns';
import { forwardRef } from 'react';
import QRCode from 'react-qr-code';

// Function to convert numbers to words (e.g., 1 to "one")
const numberToWords = (num) => {
   if (num === undefined || num === null) return '';

   const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
   const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

   if (num < 20) return ones[num];

   if (num < 100) {
      return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
   }

   if (num < 1000) {
      return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
   }

   return num.toString();
};

const PLTPCertificateTemplate = forwardRef(({ certificate, application, orderOfPayment, hiddenOnPrint = [] }, ref) => {
   const certificateData = certificate?.pltpCertificateData;
   const treeInventory = certificateData?.treeInventory?.[0] || {};

   // Format tree description: "one (1) blown-down Acacia (Samanea saman)"
   const treeDescription = treeInventory ?
      `${numberToWords(treeInventory.numberOfTreesInventoried)} (${treeInventory.numberOfTreesInventoried}) ${treeInventory.treeStatus?.toLowerCase()} ${treeInventory.species} (${treeInventory.scientificName})`
      : '_________________';

   // Use a direct URL instead of JSON data for better QR code compatibility across devices
   const qrValue = `${window.location.origin}/verify-certificate/${certificate?.id}`;

   const formatDate = (timestamp) => {
      try {
         if (!timestamp) return null;
         if (typeof timestamp === 'string') {
            if (!isNaN(timestamp)) {
               return format(new Date(parseInt(timestamp)), 'MMMM d, yyyy');
            }
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (typeof timestamp === 'number') {
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (timestamp instanceof Date) {
            return format(timestamp, 'MMMM d, yyyy');
         }
         return null;
      } catch (error) {
         console.error('Error formatting date:', error, 'timestamp:', timestamp);
         return null;
      }
   };

   return (
      <div ref={ref} className="p-8 bg-white">
         {/* Header */}
         <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-4 mb-4">
               <img src={DENRLogo} alt="DENR Logo" className="h-20" />
               <div>
                  <h1 className="text-xl font-bold">PRIVATE LAND TIMBER PERMIT</h1>
                  <p className="text-sm">Department of Environment and Natural Resources</p>
                  <p className="text-sm">PENRO Marinduque</p>
               </div>
               <img src={BagongPilipinasLogo} alt="GovPH Logo" className="h-20" />
            </div>
            <p className="text-sm">PLTP No. {certificate?.certificateNumber || '_________________'}</p>
         </div>

         {/* Recipient Details */}
         <div className="mb-6">
            <p className="mb-4">{certificateData?.name?.toUpperCase() || 'MRS. MERCEDITA MENDOZA'}</p>
            <p className="mb-4">{certificateData?.address || 'Brgy. Lipa, Sta. Cruz'}</p>
            <p className="mb-4">Marinduque</p>
            <p className="mb-4">Dear {certificateData?.name?.split(' ')[0] || 'Mrs. Mendoza'}:</p>
         </div>

         {/* Main Content */}
         <div className="mb-6">
            <p className="text-justify mb-4">
               This refers to your request to cut and utilize <span className="underline">{treeDescription}</span> tree situated within your private land identified as <span className="underline">Lot No. {certificateData?.lotNumber || '_________________'}</span> covered by Original Certificate of Title No. <span className="underline">{certificateData?.octNumber || '_________________'}</span> located in <span className="underline">{certificateData?.address || '_________________'}</span>.
            </p>
            <p className="text-justify mb-4">
               Pursuant to DENR Administrative Order No. 2022-10 entitled, "<span className="italic">Revised DENR Manual of Authorities on Technical Matters</span>" and considering the merit of the request and having complied with all the documentary requirements, Private Land Timber Permit is hereby granted in your favor to cut and utilize <span className="underline">{treeDescription}</span> tree within the aforementioned lot with a net harvestable volume of <span className="underline">{certificateData?.totalNetVolume?.toFixed(3) || '_________________'}</span>cu.m. after deducting 30% allowance for internal defects and harvesting inefficiency as shown in the table below.
            </p>
         </div>

         {/* Tree Inventory Table */}
         <div className="mb-8">
            <table className="w-full border-collapse border border-black">
               <thead>
                  <tr>
                     <th className="border border-black p-2">Species</th>
                     <th className="border border-black p-2">No. Of trees inventoried</th>
                     <th className="border border-black p-2">Gross Volume (cu.m.)</th>
                     <th className="border border-black p-2">Net Volume (cu.m)</th>
                  </tr>
               </thead>
               <tbody>
                  <tr>
                     <td className="border border-black p-2">{treeInventory.species || 'Acacia'}</td>
                     <td className="border border-black p-2 text-center">{treeInventory.numberOfTreesInventoried || '1'}</td>
                     <td className="border border-black p-2 text-center">{treeInventory.grossVolume?.toFixed(2) || '1.91'}</td>
                     <td className="border border-black p-2 text-center">{treeInventory.netVolume?.toFixed(3) || '1.337'}</td>
                  </tr>
                  <tr className="font-bold">
                     <td className="border border-black p-2">Total</td>
                     <td className="border border-black p-2 text-center">{certificateData?.totalTrees || '1'}</td>
                     <td className="border border-black p-2 text-center">{certificateData?.totalGrossVolume?.toFixed(2) || '1.91'}</td>
                     <td className="border border-black p-2 text-center">{certificateData?.totalNetVolume?.toFixed(3) || '1.337'}</td>
                  </tr>
               </tbody>
            </table>
         </div>

         {/* Replacement Ratio Text */}
         <div className="mb-8">
            <p className="text-justify">
               Pursuant to Memorandum Order No. 2012-02 dated November 2012 also known as "Uniform Replacement Ratio for Cut or Relocated Trees", the permittee is required to replace each tree to be cut with fifty (50) seedlings, strictly of indigenous species with at least one (1) meter in height to be donated by the permittee to DENR PENRO Marinduque in support of the National Greening Program and Climate Change initiatives of the Government.
            </p>
         </div>

         {/* Signature Section */}
         <div className="mb-8">
            <p className="mb-8">Very truly yours,</p>
            <div className="text-left">
               {certificate?.penrCenrOfficerSignature?.data && (
                  <div className="mb-2">
                     <img
                        src={`data:${certificate.penrCenrOfficerSignature.contentType};base64,${certificate.penrCenrOfficerSignature.data}`}
                        alt="PENR Officer Signature"
                        className="mx-auto h-16 object-contain"
                     />
                  </div>
               )}
               <p className="font-bold">IMELDA M. DIAZ</p>
               <p>OIC-PENR Officer</p>
            </div>
         </div>

         {/* Payment Details */}
         <div className="mb-4">
            <p>Amount paid: Php {orderOfPayment?.officialReceipt?.amount?.toFixed(2) || '_______________'}</p>
            <p>O.R. No.: {orderOfPayment?.officialReceipt?.orNumber || '_______________'}</p>
            <p>Date: {orderOfPayment?.officialReceipt?.dateIssued ? formatDate(orderOfPayment.officialReceipt.dateIssued) : '_________________'}</p>
         </div>

         {/* Footer */}
         <div className="text-xs">
            <p className="mb-4">***Not Valid without DENR PENRO Marinduque Official Dry Seal***</p>
            <p className="mb-4">This Private Land Timber Permit (PLTP) is valid for only fifty (50) days upon receipt of the applicant.</p>
            <p className="italic">
               Issued this _________________, at DENR PENRO Marinduque, Capitol Compound, Brgy. Bangbangalon, Boac, Marinduque.
            </p>
         </div>

         {/* QR Code */}
         <div className={`absolute top-4 right-4 ${hiddenOnPrint.includes('qr-code') ? 'no-print' : ''}`}>
            <QRCode
               value={qrValue}
               size={80}
               style={{ height: "auto", maxWidth: "100%", width: "100%" }}
               viewBox={`0 0 80 80`}
            />
            <p className="text-xs mt-1 text-center">Scan to verify</p>
         </div>
      </div>
   );
});

PLTPCertificateTemplate.displayName = 'PLTPCertificateTemplate';

export default PLTPCertificateTemplate;
